import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import { API_BASE_URL } from '../config/config';
import '../css/AdminScreens.css';
import { useNavigate } from 'react-router-dom';
import OpsLayout from '../components/OpsLayout';
import opsStatusTone from '../utils/opsStatusTone';
import ScreenModal from '../components/ScreenModal';

const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

const AdminScreens = () => {
    const navigate = useNavigate();
    const [screens, setScreens] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedScreen, setSelectedScreen] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const screensPerPage = 10;
    const [filterType, setFilterType] = useState('all');

    // 역할 유형 매핑
    const roleTypeMapping = {
        'MASTER': '총괄관리자',
        'SUB_MASTER': '부관리자',
        'MEMBER': '일반회원'
    };

    // 역할 코드 매핑
    const roleCodeMapping = {
        'MASTER': '총괄관리자',
        'SUB_MASTER': '그룹 관리자',
        'CENTER_ADMIN': '센터 관리자',
        'MEMBER': '일반회원'
    };

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }

        try {
            const decodedToken = jwtDecode(token);
            const currentTime = Date.now() / 1000;

            if (decodedToken.exp < currentTime) {
                localStorage.removeItem('token');
                navigate('/login');
                return;
            }
        } catch (error) {
            localStorage.removeItem('token');
            navigate('/login');
            return;
        }

        fetchScreens();
    }, [navigate]);

    useEffect(() => {
        const handleFilterChange = (event) => {
            setFilterType(event.detail.type);
        };
        window.addEventListener('filterChange', handleFilterChange);
        return () => window.removeEventListener('filterChange', handleFilterChange);
    }, []);

    const fetchScreens = async () => {
        setIsLoading(true);
        setError('');

        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_BASE_URL}/api/center-view-access-control`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            console.log('API 응답:', response.data);

            if (response.data && response.data.success && Array.isArray(response.data.screens)) {
                const formattedScreens = response.data.screens.map(screen => ({
                    ...screen,
                    granted_yn: screen.granted_yn ? 'TRUE' : 'FALSE',
                    view_cd: screen.view_cd || ''
                }));
                setScreens(formattedScreens);
            } else {
                console.error('잘못된 데이터 형식:', response.data);
                setError('데이터를 불러올 수 없습니다.');
            }
        } catch (err) {
            console.error('API 호출 오류:', err);
            setError('데이터를 불러오는 중 오류가 발생했습니다.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchScreens();
    }, [filterType]);

    const handleRowClick = (screen) => {
        setSelectedScreen(screen);
        setShowCreateModal(true);
    };

    const handleCloseModal = () => {
        setSelectedScreen(null);
        setShowCreateModal(false);
        fetchScreens();
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleSearch = () => {
        fetchScreens();
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const indexOfLastScreen = currentPage * screensPerPage;
    const indexOfFirstScreen = indexOfLastScreen - screensPerPage;
    const currentScreens = screens.slice(indexOfFirstScreen, indexOfLastScreen);
    const totalPages = Math.ceil(screens.length / screensPerPage);

    const handleDeleteScreen = async (id) => {
        if (!window.confirm('정말로 이 화면 권한을 삭제하시겠습니까?')) {
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const response = await axios.delete(`${API_BASE_URL}/api/center-view-access-control/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                fetchScreens();
            } else {
                setError('화면 권한 삭제에 실패했습니다.');
            }
        } catch (err) {
            console.error('화면 권한 삭제 오류:', err);
            setError('화면 권한 삭제 중 오류가 발생했습니다.');
        }
    };

    const formatViewCodes = (viewCodes) => {
        if (!viewCodes) return '';

        if (typeof viewCodes === 'string') {
            return viewCodes;
        }

        return Array.isArray(viewCodes) ? viewCodes.join(', ') : viewCodes;
    };

    const handlePermissionChange = async (screenId, newGrantedYn) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.put(
                `${API_BASE_URL}/api/center-view-access-control/${screenId}`,
                { granted_yn: newGrantedYn },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (response.data.success) {
                setScreens(prevScreens =>
                    prevScreens.map(screen =>
                        screen.id === screenId
                            ? { ...screen, granted_yn: newGrantedYn }
                            : screen
                    )
                );
            } else {
                setError('권한 변경에 실패했습니다.');
            }
        } catch (err) {
            console.error('권한 변경 오류:', err);
            setError('권한 변경 중 오류가 발생했습니다.');
        }
    };

    return (
        <OpsLayout
            title="화면 권한 관리"
            description="역할마다 열어 볼 수 있는 메뉴를 정해요."
            actions={
                <button className="sds-btn sds-btn--primary" onClick={() => setShowCreateModal(true)}>
                    <Plus size={16} aria-hidden="true" />새 화면 권한 생성
                </button>
            }
        >
            <section className="ops-page__card screens-page">
                <div className="sds-toolbar">
                    <input
                        className="sds-input screens-page__search"
                        placeholder="역할 유형 또는 화면 코드로 검색..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        onKeyPress={handleKeyPress}
                    />
                    <button className="sds-btn sds-btn--secondary" onClick={handleSearch}>검색</button>
                </div>

                {isLoading ? (
                    <div className="sds-notice">데이터를 불러오는 중입니다...</div>
                ) : error ? (
                    <>
                        <div className="sds-notice sds-notice--error">{error}</div>
                        <button className="sds-btn sds-btn--secondary" onClick={fetchScreens}>다시 시도</button>
                    </>
                ) : (
                    <>
                        <div className="sds-table-wrap">
                            <table className="sds-table">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>역할 유형</th>
                                        <th>역할 코드</th>
                                        <th>화면 코드</th>
                                        <th>권한 여부</th>
                                        <th>생성일</th>
                                        <th>수정일</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {screens.length > 0 ? (
                                        screens.map((screen) => {
                                            const grantedLabel = screen.granted_yn === 'TRUE' || screen.granted_yn === true ? '허용' : '거부';
                                            return (
                                                <tr
                                                    key={screen.id}
                                                    onClick={() => handleRowClick(screen)}
                                                    className="ops-page__row--clickable"
                                                >
                                                    <td>{screen.id}</td>
                                                    <td>{roleTypeMapping[screen.role_type] || screen.role_type}</td>
                                                    <td>{roleCodeMapping[screen.role_cd] || screen.role_cd}</td>
                                                    <td>{screen.view_cd}</td>
                                                    <td>
                                                        <span className={`sds-badge sds-badge--${opsStatusTone(grantedLabel)}`}>{grantedLabel}</span>
                                                    </td>
                                                    <td>{new Date(screen.created_at).toLocaleString()}</td>
                                                    <td>{new Date(screen.updated_at).toLocaleString()}</td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan="7" className="sds-empty">데이터가 없습니다.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className="sds-pagination">
                            <button
                                className="sds-pagination__btn"
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                aria-label="이전 페이지"
                            >
                                <ChevronLeft size={16} aria-hidden="true" />
                            </button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                <button
                                    key={page}
                                    className={`sds-pagination__btn${currentPage === page ? ' is-active' : ''}`}
                                    aria-current={currentPage === page ? 'page' : undefined}
                                    onClick={() => setCurrentPage(page)}
                                >
                                    {page}
                                </button>
                            ))}
                            <button
                                className="sds-pagination__btn"
                                onClick={() => setCurrentPage(prev => Math.max(1, Math.min(prev + 1, totalPages)))}
                                disabled={currentPage >= totalPages}
                                aria-label="다음 페이지"
                            >
                                <ChevronRight size={16} aria-hidden="true" />
                            </button>
                        </div>
                    </>
                )}

                {showCreateModal && (
                    <ScreenModal
                        screen={selectedScreen}
                        onClose={handleCloseModal}
                    />
                )}
            </section>
        </OpsLayout>
    );
};

export default AdminScreens;
